import { Choice } from '@thg-commerce/enterprise-components/src/transformer/productOptionsTransformer'
import { getVariantSavingValue } from '@thg-commerce/enterprise-pricing/src/getVariantSavingValue'
import { personalisationImagesTransformer } from '@thg-commerce/enterprise-product-options/src/utils/options'
import { sortPersonalisationFieldsByOrder } from '@thg-commerce/enterprise-product-options/src/utils/sortOptions'

import {
  ProductPageData,
  SelectedOptions,
} from '../../ApolloProvider/resolvers/Query/Product/ProductPage'
import {
  FreeTextProductPersonalisationField,
  MultiSelectionProductPersonalisationField,
  PersonalisationData,
  SingleSelectionProductPersonalisationField,
  SubscriptionChoice,
  SubscriptionData,
} from '../../ApolloProvider/resolvers/Types/Product'
import { ProductVariantItem } from '../../ApolloProvider/resolvers/Types/ProductVariant'
import {
  AvailabilitySource,
  ProductImage,
  ResolverVariantFieldsFragment,
  RichContentItem,
  SubscriptionContract,
  SubscriptionFrequency,
} from '../../generated/graphql'

import { productDescription } from './description'
import { productGallery } from './gallery'
import {
  MarketedSpecialOfferSummary,
  productMarketedSpecialOffer,
} from './marketedSpecialOffer'
import {
  generateAvailabilityMessage,
  ProductStockInformationConfigs,
} from './variants'

export enum SubscriptionOptionsPaymentType {
  UPFRONT = 'Upfront',
  ON_DISPATCH = 'On_Dispatch',
  ON_ORDER = 'On_Order',
}

type CombinedChoice = Choice & SubscriptionChoice

export type Variant = {
  title: string
  images: ProductImage[]
  product: ResolverVariantFieldsFragment['product'] & {
    description: ProductPageData['description']
  }
  vipPrice?: {
    displayValue: string
    scalarValue: string
    amount: string
  } | null
  price?: {
    price: {
      displayValue: string
      scalarValue: string
      amount: string
    }
    rrp: {
      displayValue: string
      scalarValue: string
      amount: string
    }
  } | null
  returnsInformation: RichContentItem | null
  deliveryInformation: RichContentItem | null
  deliveryReturnsContentDefined: boolean
  isSubscription: boolean
  isAutoRenewSubscription: boolean
  subscriptionFrequency?: SubscriptionFrequency
  subscriptionTerm?: number
  subscriptionPaymentType?: SubscriptionOptionsPaymentType | null
  subscriptionContracts: SubscriptionContract[]
  marketedSpecialOffer: MarketedSpecialOfferSummary | null
  selectedOptions?: SelectedOptions | null
  choices: CombinedChoice[]
  availabilitySource: AvailabilitySource
  personalisationFields?: [
    | MultiSelectionProductPersonalisationField
    | FreeTextProductPersonalisationField
    | SingleSelectionProductPersonalisationField,
  ]
  personalisationData?: PersonalisationData
}

type Product = {
  sku: number
  title: string
  linkedOn?: string | null
  content: {
    key: string
    value:
      | {
          stringListValue: string[]
        }
      | { richContentValue: { content: { content: string }[] } }
  }[]
}

type InputVariant = Variant & {
  product?: Product | null
}

type ProductWithVariants = Product & {
  defaultVariant?: Variant | null
  variants: InputVariant[]
}

export const defaultVariant = (
  product: ProductWithVariants,
  tags: string[],
  descriptionKeys?: {
    items: string[]
    details: string[]
    video: string[]
    download: string[]
  },
  productPersonalisationThemeOrder?: string[],
  productStockInformation?: ProductStockInformationConfigs,
): (Variant | InputVariant) & {
  gallery: ReturnType<typeof productGallery>
  subscriptionData: SubscriptionData
  enableAddToBasket: boolean
} => {
  let selectedOptions = {}
  const variants = product?.variants || []
  const subscriptionOptions = {
    upfront: variants.reduce(
      (subscriptionChoices: SubscriptionChoice[], variant) => {
        if (
          variant.subscriptionPaymentType ===
          SubscriptionOptionsPaymentType.UPFRONT
        ) {
          subscriptionChoices.push({
            ...variant.choices[0],
            price: variant.price?.price.displayValue,
            saving: variant.price ? getVariantSavingValue(variant.price) : null,
          })
        }
        return subscriptionChoices
      },
      [],
    ),
    onDispatch: variants.reduce(
      (subscriptionChoices: SubscriptionChoice[], variant) => {
        if (
          variant.subscriptionPaymentType ===
          SubscriptionOptionsPaymentType.ON_DISPATCH
        ) {
          subscriptionChoices.push({
            ...variant.choices[0],
            price: variant.price?.price.displayValue,
            saving: variant.price ? getVariantSavingValue(variant.price) : null,
          })
        }
        return subscriptionChoices
      },
      [],
    ),
  }
  if (product.defaultVariant) {
    // @TODO: REBUILD-8913 - remove when all sites using new product options
    if (product.defaultVariant.choices) {
      selectedOptions = product.defaultVariant.choices.reduce<SelectedOptions>(
        (accumulator, choice) => {
          if (choice.optionKey === product.linkedOn) {
            accumulator[choice.optionKey] = choice.key
          }
          return accumulator
        },
        {},
      )
    }

    const personalisationFields = product.defaultVariant.personalisationFields?.map(
      (personalisationField) => {
        if (
          personalisationField.__typename !==
          'FreeTextProductPersonalisationField'
        ) {
          return {
            ...personalisationField,
            choices: personalisationField.options.map((option) => {
              return {
                key: option.previewAssetSetIdentifier || option.name,
                colour: '',
                title: option.value,
                disabled: false,
                image: option.displayAsset?.url || '',
              }
            }),
          }
        }
        return personalisationField
      },
    )

    return {
      ...product.defaultVariant,
      selectedOptions,
      personalisationFields: sortPersonalisationFieldsByOrder(
        personalisationFields,
        productPersonalisationThemeOrder,
      ),
      personalisationData: {
        ...product.defaultVariant.personalisationData,
        personalisationSupportImages: personalisationImagesTransformer(
          product.defaultVariant,
        ),
      },
      enableAddToBasket: true,
      linkedOn: product.linkedOn,
      gallery: productGallery(product.defaultVariant, tags),
      price: product.defaultVariant.price?.price
        ? {
            ...product.defaultVariant.price,
            price:
              product.defaultVariant?.vipPrice ??
              product.defaultVariant.price?.price,
          }
        : null,
      vipPrice: product.defaultVariant?.vipPrice
        ? product.defaultVariant?.vipPrice
        : null,
      returnsInformation: product.defaultVariant.returnsInformation
        ?.content?.[0]?.content
        ? product.defaultVariant.returnsInformation.content[0].content
        : null,
      deliveryInformation: product.defaultVariant.deliveryInformation
        ?.content?.[0]?.content
        ? product.defaultVariant.deliveryInformation.content[0].content
        : null,
      deliveryReturnsContentDefined:
        product.defaultVariant.deliveryInformation?.content?.[0]?.content &&
        product.defaultVariant.returnsInformation?.content?.[0]?.content
          ? true
          : false,
      subscriptionData: {
        ...getSubscriptionData(product.defaultVariant),
        subscribeOptions: subscriptionOptions,
      },
      marketedSpecialOffer: product.defaultVariant?.marketedSpecialOffer
        ? productMarketedSpecialOffer(product.defaultVariant)
        : null,
      availabilityMessage: generateAvailabilityMessage(
        product.defaultVariant,
        productStockInformation,
      ),
      availabilitySource: product.defaultVariant.availabilitySource
        ? product.defaultVariant.availabilitySource
        : AvailabilitySource.Warehouse,
      product: {
        ...product.defaultVariant.product,
        description: descriptionKeys
          ? productDescription(product, descriptionKeys)
          : null,
        linkedOn: product.linkedOn,
      },
    }
  }

  const masterSKU = product.sku
  const masterProductVariant = product.variants.find(
    (variant) => variant.product?.sku === masterSKU,
  )

  const defaultVariant = masterProductVariant || product.variants[0]

  if (defaultVariant && defaultVariant.choices) {
    // @TODO: REBUILD-8913: remove after all sites using new product options
    selectedOptions = defaultVariant.choices.reduce<SelectedOptions>(
      (accumulator, choice) => {
        if (choice.optionKey === product.linkedOn) {
          accumulator[choice.optionKey] = choice.key
        }
        return accumulator
      },
      {},
    )
  }

  return {
    ...defaultVariant,
    selectedOptions,
    enableAddToBasket: true,
    gallery: productGallery(defaultVariant, tags),
    returnsInformation: defaultVariant.returnsInformation?.content?.[0]?.content
      ? defaultVariant.returnsInformation.content[0].content
      : null,
    deliveryInformation: defaultVariant.deliveryInformation?.content?.[0]
      ?.content
      ? defaultVariant.deliveryInformation.content[0].content
      : null,
    deliveryReturnsContentDefined:
      defaultVariant.deliveryInformation?.content?.[0]?.content &&
      defaultVariant.returnsInformation?.content?.[0]?.content
        ? true
        : false,
    price: defaultVariant.price?.price
      ? {
          ...defaultVariant.price,
          price: defaultVariant?.vipPrice ?? defaultVariant.price?.price,
        }
      : null,
    vipPrice: defaultVariant?.vipPrice ?? null,
    subscriptionData: getSubscriptionData(defaultVariant),
    marketedSpecialOffer: defaultVariant?.marketedSpecialOffer
      ? productMarketedSpecialOffer(defaultVariant)
      : null,
    product: {
      ...defaultVariant.product,
      description: descriptionKeys
        ? productDescription(product, descriptionKeys)
        : null,
      linkedOn: product.linkedOn,
    },
  }
}

const getSubscriptionData = (
  variant: Variant | InputVariant,
): SubscriptionData => {
  return {
    isSubscription: variant.isSubscription ?? false,
    isAutoRenewSubscription: variant.isAutoRenewSubscription ?? false,
    subscriptionFrequency: variant.subscriptionFrequency ?? null,
    subscriptionTerm: variant.subscriptionTerm ?? null,
    subscriptionPaymentType: variant.subscriptionPaymentType ?? null,
    subscriptionContracts: variant?.subscriptionContracts ?? [],
    choices:
      variant.isSubscription && variant.choices.length
        ? [
            {
              ...variant.choices[0],
              price: variant.price?.price.displayValue,
              saving: variant.price
                ? getVariantSavingValue(variant.price)
                : null,
            },
          ]
        : [],
  }
}

export type ProductWithDefaultVariant = {
  sku: number
  variants: ProductVariantItem[]
  defaultVariant?: ProductVariantItem
}

export const getDefaultVariantNew = (product?: ProductWithDefaultVariant) => {
  if (!product) {
    return null
  }

  if (product.defaultVariant) {
    return product.defaultVariant
  }

  const masterSKU = product.sku
  const masterProductVariant = product.variants.find(
    (variant) => variant.product?.sku === masterSKU,
  )
  const defaultVariant = masterProductVariant || product.variants[0]
  return defaultVariant
}
